import { Box, Button, Modal, Steps } from '@ksyos/design-system';
import { useState } from 'react';
import { translateKey } from '@frontend/translate';
import { CostAndPayment } from './CostAndPayment';
import { PatientInvoicePreference } from './PatientInvoicePreference';
import { useConstInfoAndConsentModalState } from './useCostInfoAndConsentModalState';

interface ICostInfoAndConsentModalProps {
    employmentId?: string;
    isOpen: boolean;
    onCancel: () => void;
    onSubmit: (consent: boolean) => void;
    patientId: string;
    practitionerId?: string;
    price: number;
    serviceName: string;
    vatRate: number;
}

export enum CostInfoAndConsentModalSteps {
    costAndPayment = 0,
    patientInvoicePreference = 1,
}

export function CostInfoAndConsentModal(props: ICostInfoAndConsentModalProps) {
    const [currentStep, setCurrentStep] = useState(CostInfoAndConsentModalSteps.costAndPayment);
    const [showNoConsentAlert, setShowNoConsentAlert] = useState<boolean | undefined>(false);

    const steps = [
        // TODO: add first step when build in story CS-15757
        { title: translateKey('costInfoAndConsentModal.costInfo') },
        { title: translateKey('costInfoAndConsentModal.invoice') },
    ];

    const { handleChange, handleBlur, state, submit, validationErrors } = useConstInfoAndConsentModalState(
        props.patientId,
        props.onSubmit
    );

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => props.onCancel()}
            size="large"
        >
            <Modal.Header>{renderHeaderTitle()}</Modal.Header>
            <Modal.CloseButton></Modal.CloseButton>
            <Modal.Body>
                <Steps
                    activeStep={currentStep}
                    sx={{ mb: 10 }}
                >
                    {steps.map((step, index) => (
                        <Steps.Step
                            key={step.title}
                            label={step.title}
                            onClick={() => setCurrentStep(index)}
                        />
                    ))}
                </Steps>
                <Box minHeight="470px">{renderSteps(currentStep)}</Box>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => props.onCancel()}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>
                <Button onClick={handleOnClickNextStep}>
                    {translateKey(
                        currentStep === CostInfoAndConsentModalSteps.patientInvoicePreference
                            ? 'common.start'
                            : 'common.next'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function renderSteps(currentStepNumber: number) {
        if (currentStepNumber === CostInfoAndConsentModalSteps.costAndPayment) {
            return (
                <CostAndPayment
                    onchange={handleChange}
                    price={props.price}
                    serviceName={props.serviceName}
                    showNoConsentAlert={showNoConsentAlert}
                    state={state}
                    validationErrors={validationErrors}
                    vatRate={props.vatRate}
                />
            );
        }
        if (currentStepNumber === CostInfoAndConsentModalSteps.patientInvoicePreference) {
            return (
                <PatientInvoicePreference
                    onBlur={handleBlur}
                    onChange={handleChange}
                    state={state}
                    validationErrors={validationErrors}
                />
            );
        }
    }

    async function handleNextStep() {
        if (currentStep < CostInfoAndConsentModalSteps.patientInvoicePreference) {
            setCurrentStep(currentStep + 1);

            return;
        }

        await submit();
    }

    function handleOnClickNextStep() {
        if (currentStep === CostInfoAndConsentModalSteps.costAndPayment) {
            if (showNoConsentAlert && !state.hasCostConsentPatient) {
                return null;
            }
            state.hasCostConsentPatient ? handleNextStep() : setShowNoConsentAlert(true);

            return;
        }

        if (validationErrors.size === 0) {
            handleNextStep();
        }
    }

    function renderHeaderTitle() {
        return translateKey(`costInfoAndConsentModal.headerStep${currentStep}`);
    }
}
